import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Input from "../../../components/ui/Input";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  deleteProductService,
  getAllProductsService,
  getProductByIdService,
  updateProductService,
} from "redux/services/products_sevice";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { useNavigate } from "react-router-dom";

const StatusBadge = ({ type, label }) => {
  const map = {
    success: "bg-success text-success-foreground",
    warning: "bg-warning text-warning-foreground",
    error: "bg-error text-error-foreground",
  };

  return (
    <span
      className={`px-3 text-xs rounded-full inline-flex items-center justify-center whitespace-nowrap ${map[type]}`}
      style={{ height: 20 }}
    >
      {label}
    </span>
  );
};

const ProductTable = ({
  products,
  selectedProducts,
  onProductSelect,
  onSelectAll,
  onProductEdit,
  onProductView,
  sortConfig,
  onSort,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const handleInlineEdit = async (product) => {
    setEditingId(product.id);
    setLoadingEdit(true);
    await dispatch(getProductByIdService(product.id));
    setLoadingEdit(false);
    navigate("/edit-product");
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteProductService(deleteProductId)).unwrap();
      toast.success(t("تم الحذف بنجاح"));
      dispatch(getAllProductsService({ page: 1 }));
    } catch {
      toast.error(t("فشل في الحذف"));
    } finally {
      setShowDeleteModal(false);
      setDeleteProductId(null);
    }
  };

  const stockBadge = (qty) => {
    if (qty === 0)
      return <StatusBadge type="error" label={t("outOfStock")} />;
    if (qty <= 10)
      return <StatusBadge type="warning" label={t("lowStock")} />;
    return <StatusBadge type="success" label={t("inStock")} />;
  };

  /* =========================
     DESKTOP TABLE
  ========================= */
  const DesktopTable = () => (
    <table className="w-full text-sm hidden md:table">
      <thead className="bg-green-50 font-semibold">
        <tr>
          <th className="p-4">{t("product")}</th>
          <th className="p-4">{t("category")}</th>
          <th className="p-4">{t("price")}</th>
          <th className="p-4">{t("priceAfterDiscount")}</th>
          <th className="p-4">{t("quantity")}</th>
          <th className="p-4">{t("status")}</th>
          <th className="p-4">{t("actions")}</th>
        </tr>
      </thead>

      <tbody>
        {products.map((p) => (
          <tr key={p.id} className="border-b hover:bg-muted/40">
            <td className="p-4">
              <div className="flex items-center gap-3">
                <Image
                  src={p.images?.[0]?.file_path}
                  className="w-10 h-10 rounded object-cover"
                />
                <button
                  onClick={() => onProductView(p)}
                  className="font-medium"
                >
                  {p.translations?.ar?.name}
                </button>
              </div>
            </td>

            <td className="p-4">
              {p.categories?.map((c) => (
                <div key={c.id}>{c.translations?.ar?.name}</div>
              ))}
            </td>

            <td className="p-4">{p.price}</td>
            <td className="p-4">{p.price_after_discount}</td>
            <td className="p-4">{p.variants?.[0]?.quantity}</td>
            <td className="p-4">{stockBadge(p.variants?.[0]?.quantity)}</td>

            <td className="p-4 flex gap-2">
              <button onClick={() => handleInlineEdit(p)}>
                <Icon name="Edit2" size={16} />
              </button>
              <button
                onClick={() => {
                  setDeleteProductId(p.id);
                  setShowDeleteModal(true);
                }}
                className="text-error"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  /* =========================
     MOBILE CARDS
  ========================= */
  const MobileCards = () => (
    <div className="space-y-4 md:hidden">
      {products.map((p) => (
        <div
          key={p.id}
          className="bg-white rounded-xl border p-4 space-y-3"
        >
          <div className="flex gap-3">
            <Image
              src={p.images?.[0]?.file_path}
              className="w-14 h-14 rounded object-cover"
            />
            <div className="flex-1">
              <div className="font-semibold">
                {p.translations?.ar?.name}
              </div>
              <div className="text-xs text-muted-foreground">
                {p.categories?.[0]?.translations?.ar?.name}
              </div>
            </div>
            {stockBadge(p.variants?.[0]?.quantity)}
          </div>

          <div className="grid grid-cols-2 text-sm gap-y-2">
            <div>{t("price")}:</div>
            <div>{p.price}</div>

            <div>{t("priceAfterDiscount")}:</div>
            <div>{p.price_after_discount}</div>

            <div>{t("quantity")}:</div>
            <div>{p.variants?.[0]?.quantity}</div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => handleInlineEdit(p)}>
              <Icon name="Edit2" size={18} />
            </button>
            <button
              onClick={() => {
                setDeleteProductId(p.id);
                setShowDeleteModal(true);
              }}
              className="text-error"
            >
              <Icon name="Trash2" size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <DesktopTable />
      <MobileCards />

      {showDeleteModal && (
        <DeleteConfirmationModal
          isOpen
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};

export default ProductTable;
